import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import { TableSimpleCellProps } from "ds/components/TableSimple/types";
import { Module, VersionState } from "types/generated";
import TableSimpleCell from "ds/components/TableSimple/Cell";
import useCanEnableModule from "shared/Module/access/useCanEnableModule";
import useCanDisableModule from "shared/Module/access/useCanDisableModule";
import useSetModuleFavorite from "shared/Module/useSetModuleFavorite";
import DisableButton from "views/Account/Module/Versions/Components/DisableButton";
import EnableButton from "views/Account/Module/Versions/Components/EnableButton";

import { canAccessModule } from "../../helpers";
import { SHARED_MODULE_ACCESS_INFO } from "../../constants";
import { showModuleVersionInstructionsDrawer } from "../../InstructionsDrawer";

const REFETCH_QUERIES = ["SearchModulesNew", "SearchModulesSuggestionsNew"];

const ModulesTableRowActionsCell = ({
  item: module,
  ...cellProps
}: TableSimpleCellProps<Module>) => {
  const canEnableModule = useCanEnableModule()(module);
  const canDisableModule = useCanDisableModule()(module);
  const isDisabled = !canAccessModule(module);

  const moduleUrl = `/module/${module.id}`;

  const [setModuleFavorite, { loading: setFavoriteLoading }] = useSetModuleFavorite({
    refetchQueries: REFETCH_QUERIES,
  });

  const handleStarModule = () => {
    setModuleFavorite({ id: module.id, star: !module.starred });
  };

  const shouldShowInstructions =
    !module.current?.yanked && module.current?.state === VersionState.Active;

  const handleOpenInstructions = () => {
    if (!module.current?.yanked && module.current?.state === VersionState.Active) {
      showModuleVersionInstructionsDrawer({
        module,
      });
    }
  };

  return (
    <TableSimpleCell {...cellProps}>
      <DropdownMenuEllipsis tooltip="Module actions">
        <CopyFieldDropdownMenuItem title="Copy ID" value={module.id} />
        <DisableButton
          canDisable={canDisableModule}
          moduleId={module.id}
          refetchQueries={REFETCH_QUERIES}
        />
        <EnableButton
          canEnable={canEnableModule}
          moduleId={module.id}
          refetchQueries={REFETCH_QUERIES}
        />
        <DropdownMenuItem
          href={`${moduleUrl}/settings`}
          isDisabled={isDisabled}
          tooltip={SHARED_MODULE_ACCESS_INFO}
          isTooltipActive={isDisabled}
          tooltipWidthMode="maxWidthSm"
        >
          Settings
        </DropdownMenuItem>
        <DropdownMenuItem
          onAction={handleStarModule}
          loading={setFavoriteLoading}
          isDisabled={isDisabled}
          tooltip={SHARED_MODULE_ACCESS_INFO}
          isTooltipActive={isDisabled}
          tooltipWidthMode="maxWidthSm"
        >
          {module.starred ? "Remove from favorites" : "Add to favorites"}
        </DropdownMenuItem>
        <DropdownMenuItem
          onAction={handleOpenInstructions}
          isDisabled={!shouldShowInstructions}
          tooltipPlacement="bottom"
          tooltip="There are no active versions for this module"
          isTooltipActive={!shouldShowInstructions}
        >
          Show instructions
        </DropdownMenuItem>
      </DropdownMenuEllipsis>
    </TableSimpleCell>
  );
};

export default ModulesTableRowActionsCell;
